import React, { useState } from "react";
import Button from "../../../components/buttons/Button";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import CredentialSelector from "../../admin/credentialManagement/CredentialSelector";
import {
  fetchVerificationMethods,
  createGooglListing,
  checkForGoogleLocationId,
  completeVerification,
  initiateNewListingVerification
} from "./GoogleListingVerification-api";
import {
  formatAddress,
  phoneCallVerificationMethod,
  smsVerificationMethod,
  emailVerificationMethod,
  addressVerificationMethod,
  verificationMethodNames
} from "./GoogleListingHelpers";
import { withRemoteQuery } from "../../../util/withRemoteQuery";
import { Spinner } from "../../../components";
import ModalHeaderTitle from "../../../components/modal/ModalHeaderTitle";

const propTypes = {};

const defaultProps = {};

export default function AddMissingGoogleListing({ listing }) {
  const [modalOpen, setModalOpen] = useState(false);

  const [activeListingVerification, setActiveListingVerification] = useState(
    null
  );

  const toggle = () => setModalOpen(!modalOpen);

  const selectGoogleAccount = (credential) =>
    setSelectedGoogleAccount(credential);

  const [selectedGoogleAccount, setSelectedGoogleAccount] = useState(null);

  const [
    failedToCreateGoogleLocationErrorCode,
    setFailedToCreateGoogleLocationErrorCode
  ] = useState(null);

  const [
    loadingCreateGoogleLocation,
    setLoadingCreateGoogleLocation
  ] = useState(false);

  const [
    availableVerificationMethods,
    setAvailableVerificationMethods
  ] = useState(null);

  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState(
    null
  );

  const [selectedGoogleLocation, setSelectedGoogleLocation] = useState(null);

  const setGoogleVerificationMethod = (event) => {
    setSelectedVerificationMethod(event.target.value);
  };

  const [failedToVerifyError, setFailedToVerifyError] = useState(null);

  const [pin, setPin] = useState("");

  const [verifiedSuccessfully, setVerifiedSuccessfully] = useState(false);

  const [
    receiveGoogleVerificationCodeLoading,
    setReceiveGoogleVerificationCodeLoading
  ] = useState(false);

  const [
    sentVerificationCodeLoading,
    setSentVerificationCodeLoading
  ] = useState(false);

  const reset = () => {
    setSelectedGoogleAccount(null);
    setSelectedGoogleLocation(null);
    setFailedToCreateGoogleLocationErrorCode(null);
    setSelectedVerificationMethod(null);
    setAvailableVerificationMethods(null);
    setActiveListingVerification(null);
  };

  const extractVerificationMethodData = (
    selectedVerificationMethod,
    methodData
  ) => {
    switch (selectedVerificationMethod) {
      case "PHONE_CALL":
      case "SMS":
        return methodData.phoneData.phoneNumber;
      case "EMAIL":
        return `${methodData.emailData.userName}@${methodData.emailData.domainName}`;
      case "ADDRESS":
        return methodData.addressData.businessName;
    }
  };

  const sendPin = () => {
    setFailedToVerifyError(null);
    setReceiveGoogleVerificationCodeLoading(true);
    const methodData = availableVerificationMethods.find(
      (verificationMethod) =>
        verificationMethod.verificationMethod === selectedVerificationMethod
    );
    const selectedVerificationMethodData = extractVerificationMethodData(
      selectedVerificationMethod,
      methodData
    );

    initiateNewListingVerification(
      selectedGoogleAccount?._id,
      selectedGoogleLocation?.name,
      selectedVerificationMethod,
      selectedVerificationMethodData
    ).then((verification) => {
      setActiveListingVerification(verification);
      setReceiveGoogleVerificationCodeLoading(false);
    })
    .catch((e) => {
      if (e.response.status === 400) {
        // Expected errors like invalid pin number
        setFailedToVerifyError(e.response.data.error);
      } else {
        // Unhandled errors
        const error = (
          <>
            Something went wrong while we attempted to send the verification code. Please try again or contact support and provide them
            with the following error code: <b>{e.response.data.errorId}</b>.
          </>
        );

        // Users can provide this error id to support so we can handle edge cases.
        setFailedToVerifyError(error);
      }
      setReceiveGoogleVerificationCodeLoading(false);
      setSentVerificationCodeLoading(false);
    });
  };

  const enterPin = () => {
    setSentVerificationCodeLoading(true);
    setFailedToVerifyError(null);
    completeVerification(activeListingVerification._id["$oid"], pin)
      .then(() => {
        setVerifiedSuccessfully(true);
        setSentVerificationCodeLoading(false);
      })
      .catch((e) => {
        if (e.response.status === 400) {
          // Expected errors like invalid pin number
          setFailedToVerifyError(e.response.data.error);
        } else {
          // Unhandled errors
          const error = (
            <>
              Something went wrong while we attempted to verify the listing
              using the pin you entered. Please contact support and provide them
              with the following error code: <b>{e.response.data.errorId}</b>.
            </>
          );

          // Users can provide this error id to support so we can handle edge cases.
          setFailedToVerifyError(error);
        }
        setSentVerificationCodeLoading(false);
      });
  };

  const createNewGoogleLocation = () => {
    setLoadingCreateGoogleLocation(true);
    setFailedToCreateGoogleLocationErrorCode(null);
    createGooglListing(selectedGoogleAccount._id, listing.location.locationId)
      .then((googleLocation) => {
        fetchVerificationMethods(
          selectedGoogleAccount._id,
          googleLocation.name
        ).then((verificationMethods) => {
          // Just for QA
          // TODO - Remove when implementing backend
          setModalOpen(false);
          setLoadingCreateGoogleLocation(false);
          setAvailableVerificationMethods(verificationMethods);
          setSelectedGoogleLocation(googleLocation);
        });
      })
      .catch((e) => {
        // Users can provide this error id to support so we can handle edge cases.
        setLoadingCreateGoogleLocation(false);
        setFailedToCreateGoogleLocationErrorCode(e.response.data);
      });
  };

  const { loading } = withRemoteQuery({
    fetch: async () => {
      const activeLocation = await checkForGoogleLocationId(listing.locationId);
      if (!!activeLocation) {
        if (activeLocation.hasOwnProperty("googleVerification")) {
          if (activeLocation.googleVerification.state === "COMPLETED") {
            setVerifiedSuccessfully(true);
          } else {
            setActiveListingVerification(activeLocation);
          }
        } else {
          setSelectedGoogleLocation({
            name: activeLocation.googleLocationId
          });
          setSelectedGoogleAccount({
            _id: activeLocation.chatmeterCredentialId
          });
          const verificationMethods = await fetchVerificationMethods(
            activeLocation.chatmeterCredentialId,
            activeLocation.googleLocationId
          );
          setAvailableVerificationMethods(verificationMethods);
        }
      }
    }
  });

  const maybePhoneCallVerificationMethod = phoneCallVerificationMethod(
    availableVerificationMethods
  );
  const maybeSmsVerificationMethod = smsVerificationMethod(
    availableVerificationMethods
  );
  const maybeEmailVerificationMethod = emailVerificationMethod(
    availableVerificationMethods
  );
  const maybeAddressVerificationMethod = addressVerificationMethod(
    availableVerificationMethods
  );

  const defaultState = () => {
    return (
      !availableVerificationMethods &&
      !activeListingVerification &&
      !verifiedSuccessfully
    );
  };

  return (
    <div className="ml-2">
      {loading && <Spinner timeout={0} />}
      {
        !loading && <>
          {/* Verification Methods */}
          {!!availableVerificationMethods && !activeListingVerification && (
            <div data-testid="verification-methods">
              <h6>Verification Method</h6>
              <p className="content-spacer-top content-spacer-bottom">
                In order to fully manage and represent your business on Google, you
                will need to confirm you are associated with it. Select from the
                following options:
              </p>
              <form>
                {!!maybePhoneCallVerificationMethod && (
                  <div>
                    <label className="d-flex align-items-center">
                      <input
                        type="radio"
                        name="googleVerificationMethod"
                        onClick={setGoogleVerificationMethod}
                        value={"PHONE_CALL"}
                      />{" "}
                      <span className="content-spacer-left">
                        Phone Call to{" "}
                        {maybePhoneCallVerificationMethod.phoneData.phoneNumber}
                      </span>
                    </label>
                  </div>
                )}
                {!!maybeSmsVerificationMethod && (
                  <div>
                    <label className="d-flex align-items-center">
                      <input
                        data-testid="verify-by-sms"
                        type="radio"
                        name="googleVerificationMethod"
                        onClick={setGoogleVerificationMethod}
                        value={"SMS"}
                      />{" "}
                      <span className="content-spacer-left">
                        SMS to {maybeSmsVerificationMethod.phoneData.phoneNumber}
                      </span>
                    </label>
                  </div>
                )}
                {!!maybeAddressVerificationMethod && (
                  <div>
                    <label className="d-flex align-items-center">
                      <input
                        type="radio"
                        name="googleVerificationMethod"
                        onClick={setGoogleVerificationMethod}
                        value={"ADDRESS"}
                      />{" "}
                      <span className="content-spacer-left">
                        Mail postcard to{" "}
                        {maybeAddressVerificationMethod.addressData.businessName}{" "}
                        {formatAddress(
                          maybeAddressVerificationMethod.addressData.address
                        )}
                      </span>
                    </label>
                  </div>
                )}
                {!!maybeEmailVerificationMethod && (
                  <div>
                    <label className="d-flex align-items-center">
                      <input
                        type="radio"
                        name="googleVerificationMethod"
                        onClick={setGoogleVerificationMethod}
                        value={"EMAIL"}
                      />
                      <span className="content-spacer-left">
                        Email to {maybeEmailVerificationMethod.emailData.userName}@
                        {maybeEmailVerificationMethod.emailData.domainName}
                      </span>
                    </label>
                  </div>
                )}
              </form>
              <div className="d-flex flex-row">
                <button
                  className="btn btn-sm btn-outline-secondary content-spacer-top"
                  onClick={reset}
                >
                  Cancel
                </button>
                <div className="d-flex align-items-center flex-row content-spacer-top">
                  <button
                    data-testid="send-verification-method-button"
                    className="btn btn-sm btn-secondary control-spacer-left"
                    disabled={
                      !selectedVerificationMethod ||
                      receiveGoogleVerificationCodeLoading
                    }
                    onClick={sendPin}
                    value={pin}
                  >
                    Send Code
                  </button>
                  { receiveGoogleVerificationCodeLoading && (
                    <div className="d-flex align-items-center control-spacer-left">
                      <Spinner timeout={0} center />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Verification Active */}
          {!!activeListingVerification && !verifiedSuccessfully && (
            <div>
              <b className="content-spacer-top">Google User</b>
              <br />
              {activeListingVerification?.googleAccountName}
              <br />
              <br />
              <b className="content-spacer-top">Date Submitted</b>
              <br />
              {new Date(
                activeListingVerification?.googleVerification?.createTime["$date"]
              ).toLocaleDateString()}
              <br />
              <br />
              <b className="content-spacer-top">Verification Method</b>
              <br />
              {
                verificationMethodNames[
                  activeListingVerification?.googleVerification?.method
                ]
              }
              <input
                type="text"
                style={{
                  maxWidth: "383px"
                }}
                data-testid="verification-pin-input"
                placeholder="Enter Pin"
                className="form-control text-input control-spacer-top"
                onChange={(e) => setPin(e.target.value)}
              />
              <div className="d-flex align-items-center flex-row content-spacer-top">
                <button
                  className="btn btn-secondary btn-sm d-inline-flex align-items-center"
                  onClick={enterPin}
                  disabled={sentVerificationCodeLoading}
                >
                  Submit
                </button>
                {sentVerificationCodeLoading && (
                  <div className="d-flex align-items-center control-spacer-left">
                    <Spinner timeout={0} center />
                  </div>
                )}
              </div>
              {!!failedToVerifyError && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {failedToVerifyError}
                </div>
              )}
            </div>
          )}
          {/* Verified Message */}
          <>
            {verifiedSuccessfully && (
              <div data-testid="location-verified-title">
                <b className="text-success">Location Verified</b>
                <br />
                <div>
                  Listings may be reviewed for quality and can take up to 3 days to
                  be published.
                </div>
              </div>
            )}
          </>
          {defaultState() && (
            <>
              {/* Modal Trigger */}
              {!loading && <Button
                data-testid="add-listing-button"
                className="btn-sm control-spacer-bottom"
                onClick={() => setModalOpen(true)}
              >
                Create GMB Listing
              </Button>}
              {/* Add Credentials Modal */}
              <Modal isOpen={modalOpen} toggle={toggle}>
                <ModalHeader>
                  <ModalHeaderTitle title="Select GMB Account" subtitle="The listing will be verified/claimed using the account selected. The list below includes GMB accounts added to Third-Party Credentials." />
                </ModalHeader>
                <ModalBody>
                  {loadingCreateGoogleLocation ? (
                    <Spinner
                      timeout={0}
                      center
                      message="Creating Location In Google"
                    />
                  ) : (
                    <>
                      <CredentialSelector
                        selected={selectedGoogleAccount}
                        provider={["GOOGLEMAP"]}
                        placeholder="Select Google Account"
                        onSelected={selectGoogleAccount}
                      />
                      <Button
                        data-testid="create-listing-button"
                        disabled={!selectedGoogleAccount}
                        className="btn-block control-spacer-top"
                        onClick={createNewGoogleLocation}
                      >
                        Create Location
                      </Button>
                    </>
                  )}
                  {failedToCreateGoogleLocationErrorCode && (
                    <div className="control-spacer-top text-danger">
                      {failedToCreateGoogleLocationErrorCode?.message ||
                        "An error occured. Please try again."}
                    </div>
                  )}
                </ModalBody>
              </Modal>
            </>
          )}
        </>
      }
    </div>
  );
}

AddMissingGoogleListing.propTypes = propTypes;
AddMissingGoogleListing.defaultProps = defaultProps;
